import React, { Context, useContext, useEffect, useState } from "react";
import { CheckboxOld } from "../inputs/CheckboxOld";
import { DateSelectOld } from "../inputs/DatePickerOld";
import { IParametrizedFieldContext } from "../../contexts/ParametrizedFieldContext";
import { FIELD_TYPE, IReactField, IResponseField } from "../../model/reactField";
import { strings } from "../../strings/strings";
import { v4 as uuidv4 } from 'uuid';
import './index.scss';
import { ISelectItem } from "../../model/selectItem";
import { ISettingOption } from "../../model/settingItem";
import { IFormattedResponse } from "../../model/parametrizedResponse";
import { compareObjects } from "../../utils/compareObjects";
import { SelectOneInputOld } from "../inputs/SelectOneInputOld";
import { InputTextOld } from "../inputs/InputTextOld";
import { SelectAutocompleteOld } from "../inputs/SelectAutocompleteOld";
import { SelectOneRadioOld } from "../inputs/FormikRadioGroupOld";
import { useFormikContext } from "formik";
import { InputTextMaskOld } from "../inputs/InputTextMaskOld";
import { PlateNumberInputOld } from "../inputs/PlateNumberInputOld";
import { SimpleTooltip } from "../SimpleTooltip";
import { Skeleton } from '@material-ui/lab';
import { translit } from "../../utils/utils";
import { OutputLink } from "../OutputLink";
import { ErrorFieldsContext } from "../../contexts/ErrorFieldsContext";
import { ReactSVG } from "react-svg";
import info from "../../images/info.svg";

interface IParametrizedFieldProps {
    quotationName: string,
    contextName: string,
    context: Context<IParametrizedFieldContext>,
    numericSelectOptions?: () => any[];
    shortcut?: any;
    charactersToSearch?: number;
    formatResponse?: (obj: any) => IFormattedResponse;
    onChangeListener?: (val: any) => void;
    onChangeListenerPromise?: (val: any) => Promise<void>;
    convertContextNameFunction?: (name: string) => string;
    minDate?: any;
    maxDate?: any;
    mask?: string;
    placeholder?: string;
    defaultLabel?: string;
    defaultOptions?: any[];
    fieldInfoText?: string;
    fieldInfoTextLeaveTouchDelay?: number;
    fieldInfoTextPosition?: 'left' | 'info'
    messageInfoObj?: any;
    toUpperCase?: boolean;
    lazyDefaultOptions?: (query: string) => any;
    plateNumber?: boolean;
    small?: boolean;
    formatSelected?: (obj: IFormattedResponse) => ISettingOption;
    contextPath?: string;
    selectOneRadio?: boolean;
    formatOptions?: (options: ISelectItem[]) => ISelectItem[];
    alwaysHidden?: boolean;  //deprecate
    extFieldToUpdate?: IResponseField;  //deprecate
    setExtFieldToUpdate?: any;  //deprecate
    tooltipLabel?: string;
    additionalTooltip?: React.ReactElement;
    disabled?: boolean;
    additionalFieldInfo?: string;
    defaultFieldNotRequired?: boolean; //todo Удалить этот атрибут. Переделать что бы при получении meta информации - получали список персон, а не одну. RFC-55568
    isProcessingOpenOnUpdate?: boolean,
    listenerDependency?: any,
    payerIsPersonValue?: any,
    mustBeRegisteredAlways?: boolean
}

export const ParametrizedFieldOld: React.FC<IParametrizedFieldProps> = (props) => {
    const {
        context,
        quotationName,
        contextName,
        numericSelectOptions,
        defaultOptions,
        shortcut,
        charactersToSearch,
        formatResponse,
        onChangeListener,
        onChangeListenerPromise,
        convertContextNameFunction,
        minDate,
        maxDate,
        mask,
        placeholder,
        defaultLabel,
        fieldInfoText,
        fieldInfoTextLeaveTouchDelay,
        messageInfoObj,
        formatOptions,
        toUpperCase,
        lazyDefaultOptions,
        plateNumber,
        small,
        formatSelected,
        contextPath,
        selectOneRadio,
        alwaysHidden,
        extFieldToUpdate,
        setExtFieldToUpdate,
        defaultFieldNotRequired,
        tooltipLabel,
        additionalTooltip,
        disabled,
        additionalFieldInfo,
        isProcessingOpenOnUpdate,
        listenerDependency,
        payerIsPersonValue,
        mustBeRegisteredAlways,
        fieldInfoTextPosition
    } = props;
    const {
        getFieldByName, updateField,
        getFunctionSelectItemsByFieldNameAndFormName, fields,
        registerField, addToContextFieldNames,registeredFields
    } = useContext(context);

    const defaultSettingOption = { label: strings.notChosen, value: '00000000-0000-0000-0000-000000000000' };
    const [fieldInfo, setFieldInfo] = useState<IReactField | undefined>();
    const [fieldToUpdate, setFieldToUpdate] = useState<IResponseField>(null);
    const { values } = useFormikContext();
    const { errorFields } = useContext(ErrorFieldsContext);

    useEffect(() => {
        if (fieldToUpdate) {
            updateField(fieldToUpdate.name, fieldToUpdate.value, values, contextPath, !!fieldToUpdate.isProcessingOpenOnUpdate).then(() => {
                onChangeListenerPromise?.(fieldToUpdate.value);
                setFieldToUpdate(null)
            });
        }
        //eslint-disable-next-line
    }, [fieldToUpdate]);

    useEffect(() => {
        const foundField = getFieldByName(contextName, true, contextPath);
        if (foundField && (fieldInfo === undefined || !compareObjects(fieldInfo, foundField.value))) {
            setFieldInfo(foundField.value);
        } else {
            addToContextFieldNames(contextName, contextPath);
        }
        //eslint-disable-next-line
    }, [fields]);

    // todo delete after all forms update
    useEffect(() => {
        if( mustBeRegisteredAlways && registerField ){
            if( registeredFields.every((field) => field.name !== contextName )){
                registerField(fieldInfo, contextPath)
            }
        }
    }, [registeredFields?.length, mustBeRegisteredAlways, fieldInfo, contextPath])

    // todo delete after all forms update
    useEffect(() => {
        fieldInfo && registerField?.(fieldInfo, contextPath);
        //eslint-disable-next-line
    }, [fieldInfo]);

    const onAutocompleteLazyLoading = async (query: string, newValue?: string): Promise<ISettingOption[]> => {
        if (fieldInfo?.name) {
            const res: Array<ISelectItem> = await getFunctionSelectItemsByFieldNameAndFormName(fieldInfo.name,
                // @ts-ignore send quotation if exists
                "mainContract", query, values.quotation);
            return [
                ...res.map(item => ({ value: item.value, label: item.label }))
            ]
        }

        return [defaultSettingOption];
    }

    const getFormattedOptions = (selectItems?: any) =>
        defaultOptions || (formatOptions ? formatOptions(selectItems) : selectItems);

    const getTransformedName = (name: string) =>
        convertContextNameFunction ? convertContextNameFunction(name) : name.replaceAll("-", ".")

    const getIsDisabled = (fieldInfo: IReactField) =>
        fieldInfo.isDisabled || disabled === true;

    const renderField = (id: string, fieldInfo:IReactField) => {
        const {
            inputLabel,
            name,
            value,
            type,
            selectItems,
            isRequired,
            maxLength,
            isError,
            errorMessage,
            isHidden,
            beforeInputRangeMinValue,
            beforeInputRangeMaxValue,
            businessRuleDescriptions
        } = fieldInfo;

        const label = defaultLabel || inputLabel;
        const options = getFormattedOptions(selectItems);
        const isDisabled = getIsDisabled(fieldInfo);
        const hidden = alwaysHidden || isHidden;

        const minDateValue = minDate ?? beforeInputRangeMinValue;
        const maxDateValue = maxDate ?? beforeInputRangeMaxValue;
        const businessRuleDescription = businessRuleDescriptions?.[0]?.description;
        const transformedName = getTransformedName(name);

        if (isError) errorFields.push(id);

        switch (type) {
            case FIELD_TYPE.INPUT_TEXT:
            case FIELD_TYPE.INPUT_TEXT_PHONE:
                if (plateNumber) {
                    return <PlateNumberInputOld
                        label={label}
                        disabled={isDisabled}
                        hidden={hidden}
                        error={isError}
                        errorMessage={errorMessage}
                        name={quotationName + transformedName}
                        backEndName={name}
                        setFieldToUpdate={setFieldToUpdate}
                        required={isRequired}
                        placeholder={placeholder}
                        formatResponse={formatResponse}
                        small={small}
                    />
                } else if (mask !== undefined) {
                    return <InputTextMaskOld
                        mask={mask}
                        label={label}
                        disabled={isDisabled}
                        hidden={hidden}
                        error={isError}
                        errorMessage={errorMessage}
                        name={quotationName + transformedName}
                        onChangeListener={onChangeListener}
                        backEndName={name}
                        setFieldToUpdate={setFieldToUpdate}
                        required={isRequired}
                        placeholder={placeholder}
                        toUpperCase={toUpperCase}
                        listenerDependency={listenerDependency}
                    />
                } else {
                    return <InputTextOld
                        label={label}
                        disabled={isDisabled}
                        hidden={hidden}
                        error={isError}
                        errorMessage={errorMessage}
                        onChangeListener={onChangeListener}
                        name={quotationName + transformedName}
                        backEndName={name}
                        setFieldToUpdate={setFieldToUpdate}
                        listenerDependency={listenerDependency}
                        type={'TEXT'}
                        required={isRequired}
                        placeholder={placeholder}
                    />
                }
            case FIELD_TYPE.ENUM_SELECT:
            case FIELD_TYPE.DICTIONARY_SELECT:
            case FIELD_TYPE.ELASTIC_DICTIONARY:
                    if (selectOneRadio) {
                        return <div className={'select-one-wrapper'} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            width: '100%',
                            alignItems: 'center'
                        }}>
                            <SelectOneRadioOld
                                value={value}
                                backEndName={name}
                                isProcessingOpenOnUpdate={isProcessingOpenOnUpdate}
                                name={quotationName + transformedName}
                                options={[...options.map(option => ({
                                    label: option.label,
                                    value: option.value
                                }))]}
                                setFieldToUpdate={setFieldToUpdate}
                                onChangeListener={onChangeListener} />
                            {additionalTooltip}
                            <SimpleTooltip id={'travel_is'}
                                placement={'top'}
                                title={<>{businessRuleDescription}</>}>
                                {tooltipLabel}
                            </SimpleTooltip>
                        </div>
                    } else return <SelectOneInputOld
                        required={isRequired}
                        placeholder={label}
                        backEndName={name}
                        name={quotationName + transformedName}
                        options={[...options.map(option => ({ label: option.label, value: option.value }))]}
                        className={'maxWidthForInput'}
                        disabled={isDisabled}
                        hidden={hidden}
                        errorMessage={errorMessage}
                        value={value}
                        setFieldToUpdate={setFieldToUpdate}
                        onChangeListener={onChangeListener}
                        formatResponse={formatResponse} />

            case FIELD_TYPE.BOOLEAN_SELECT:
                    return <CheckboxOld
                        contextPath={contextPath}
                        fieldValue={value === 'true'}
                        backEndName={name}
                        name={quotationName + transformedName}
                        setFieldToUpdate={setFieldToUpdate}
                        label={label}
                        disabled={isDisabled || Boolean(fieldToUpdate)}
                        onChangeListener={onChangeListener}
                        payerIsPersonValue={payerIsPersonValue}
                        hidden={hidden} />;


            case FIELD_TYPE.INPUT_NUMBER:
                if (numericSelectOptions) {
                    let arr = [];
                    numericSelectOptions().forEach(el => arr.push({ "label": el, "value": el }));
                    return <SelectOneInputOld
                        required={isRequired}
                        placeholder={label}
                        backEndName={name}
                        name={quotationName + transformedName}
                        onChangeListener={onChangeListener}
                        options={arr}
                        className={'maxWidthForInput'}
                        disabled={isDisabled}
                        hidden={hidden}
                        errorMessage={errorMessage}
                        setFieldToUpdate={setFieldToUpdate}
                        formatResponse={formatResponse}
                    />
                }
                return <InputTextOld
                    required={isRequired}
                    label={name}
                    disabled={isDisabled}
                    hidden={hidden}
                    error={isError}
                    errorMessage={errorMessage}
                    name={quotationName + transformedName}
                    backEndName={name}
                    setFieldToUpdate={setFieldToUpdate}
                    maxLength={maxLength}
                    type={'NUMBER'}
                />;
            case FIELD_TYPE.INPUT_DECIMAL_NUMBER:
                    return <InputTextOld
                        required={isRequired}
                        label={name}
                        disabled={isDisabled}
                        hidden={hidden}
                        error={isError}
                        errorMessage={errorMessage}
                        name={quotationName + transformedName}
                        backEndName={name}
                        setFieldToUpdate={setFieldToUpdate}
                        maxLength={maxLength}
                        type={'NUMBER'} />;
            case FIELD_TYPE.DATE:
                    return <DateSelectOld required={isRequired}
                        contextPath={contextPath}
                        minDate={minDateValue}
                        maxDate={maxDateValue}
                        placeholder={label}
                        name={quotationName + transformedName}
                        backEndName={name}
                        label={label}
                        disabled={isDisabled}
                        hidden={hidden}
                        setFieldToUpdate={setFieldToUpdate}
                        formatResponse={formatResponse}
                        onChangeListener={onChangeListener}
                        errorMessage={errorMessage}
                        error={isError}
                        defaultFieldNotRequired={defaultFieldNotRequired}
                        defaultValue={value} />;
            case FIELD_TYPE.FUNCTION_SELECT:
            case FIELD_TYPE.AUTOCOMPLETE:
                    return <SelectAutocompleteOld
                        contextPath={contextPath}
                        required={isRequired}
                        placeholder={label}
                        name={quotationName + transformedName}
                        backEndName={name}
                        asOption={(option: any) => {
                            return {
                                label: option.label,
                                value: option
                            }
                        }}
                        fromOption={option => option}
                        charactersToSearch={charactersToSearch}
                        loadFunction={lazyDefaultOptions ?? onAutocompleteLazyLoading}
                        noOptionsText={strings.noOptionsText}
                        shortcutData={shortcut}
                        disabled={isDisabled}
                        hidden={hidden}
                        setFieldToUpdate={setFieldToUpdate}
                        formatResponse={formatResponse}
                        errorMessage={errorMessage}
                        error={isError}
                        formatSelected={formatSelected}
                        extFieldToUpdate={extFieldToUpdate}
                        setExtFieldToUpdate={setExtFieldToUpdate}
                        onChangeListener={onChangeListener}
                    />

            case FIELD_TYPE.CHECKBOX_MENU:
                    return <div>Unknown type</div>
        }
    }

    let id = 'parametrized-field_' + uuidv4();

    if( !fieldInfo ){
        return <Skeleton variant="text" />
    }
    return (
        <div id={id} className={`parametrized-field${fieldInfo.isHidden ? ' parametrized-field--hidden' : ''}`}>
            {renderField(id, fieldInfo)}
            {fieldInfoText && fieldInfoTextPosition !== 'info' && (
                <SimpleTooltip id={translit(fieldInfoText)} leaveTouchDelay={fieldInfoTextLeaveTouchDelay} title={
                    <span>{messageInfoObj}</span>}
                >
                    <div className='input-text__field-info'>
                        <OutputLink label={fieldInfoText} variant={"grey"} position={fieldInfoTextPosition}/>
                    </div>
                </SimpleTooltip>
            )}
            {additionalFieldInfo &&
                <div className="parametrized-field__field-description">
                    {additionalFieldInfo}
                </div>}
            {fieldInfoTextPosition === 'info' && (
                <SimpleTooltip id={`tooltip_${id}`} title={messageInfoObj} placement={"top"}>
                    <div className={`parametrized-field_info-tooltipWrapper parametrized-field_info-tooltipWrapper-${fieldInfo.type}`}>
                        <ReactSVG src={info} />
                    </div>
                </SimpleTooltip>
                
            )}
        </div>
    )
}
